/**
 * ArchitecturalFormBuilder - customize labels, field visibility,
 * and enable multi-type AR selection mode.
 */
import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Paper, Snackbar, Alert, Grid, Divider,
  Switch, Chip, Tabs, Tab, FormControlLabel,
} from "@mui/material";
import SaveIcon      from "@mui/icons-material/Save";
import PreviewIcon   from "@mui/icons-material/Preview";
import LabelIcon     from "@mui/icons-material/Label";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TuneIcon      from "@mui/icons-material/Tune";
import api from "../../utils/api";

const DEFAULT_CONFIG = {
  title:          "Add Architectural Request",
  subtitle:       "Create a new architectural request and upload the required supporting documentation.",
  submit_label:   "Submit Request",
  step_labels:    ["Request Info", "Details & Files", "Review & Submit"],
  step_headings:  ["Request Information", "Details & Files", "Review & Acknowledge"],
  step_subtitles: [
    "Select the request type, subtype, and any required color or scheme details.",
    "Add request details, timeline, supporting files, and optional contractor information.",
    "Confirm the request details and acknowledge the certification before submitting.",
  ],
  fields: {
    request_type:true, subtype:true, scheme:true, color:true,
    description:true, comment:true, start_date:true, end_date:true,
    photo_front:true, photo_impact:true, documents:true, contractor:true, notes:true,
  },
  field_labels: {
    request_type:  "Request Type",
    subtype:       "Subtype",
    scheme:        "Scheme",
    color:         "Color",
    description:   "Description",
    comment:       "Comment",
    start_date:    "Anticipated Start Date",
    end_date:      "Anticipated Completion Date",
    photo_front:   "Color Photo of Front of Property",
    photo_impact:  "Color Photo of Impact Area",
    documents:     "Supporting Documents",
    contractor:    "Contractor Information",
    notes:         "Notes",
  },
  multi_type_enabled: false,
};

const FIELD_GROUPS = [
  {
    step: "Step 1 - Request Info",
    fields: [
      { key:"request_type", required:true },
      { key:"subtype" },
      { key:"scheme" },
      { key:"color" },
    ],
  },
  {
    step: "Step 2 - Details & Files",
    fields: [
      { key:"description", required:true },
      { key:"comment" },
      { key:"start_date" },
      { key:"end_date" },
      { key:"photo_front" },
      { key:"photo_impact" },
      { key:"documents" },
      { key:"contractor" },
      { key:"notes" },
    ],
  },
];

const cardSx  = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };
const primaryBtnSx = {
  backgroundColor:"#2f63ad", color:"white", fontWeight:700,
  borderRadius:"10px", textTransform:"none", boxShadow:"none",
  "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" },
};

const PreviewField = ({ label, required }) => (
  <Box sx={{ mb:1.25, height:34, borderRadius:"8px", border:"1px solid #d0d7e2",
    bgcolor:"white", display:"flex", alignItems:"center", px:1.5 }}>
    <Typography variant="caption" sx={{ color:"#9ca3af" }}>
      {label}{required?" *":""}
    </Typography>
  </Box>
);

const ArchitecturalFormBuilder = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [saving, setSaving]  = useState(false);
  const [snack,  setSnack]   = useState({ open:false, msg:"", sev:"success" });
  const [tab,    setTab]     = useState(0); // 0=Labels, 1=Visibility, 2=Settings
  const [previewStep, setPreviewStep] = useState(0);

  useEffect(() => {
    api.get("/ar-form-config").then(r => {
      if (r.data && Object.keys(r.data).length > 0) {
        setConfig(prev => ({
          ...DEFAULT_CONFIG, ...r.data,
          fields:       { ...DEFAULT_CONFIG.fields,       ...(r.data.fields || {}) },
          field_labels: { ...DEFAULT_CONFIG.field_labels, ...(r.data.field_labels || {}) },
        }));
      }
    }).catch(() => {});
  }, []);

  const setTop         = (k,v) => setConfig(c => ({ ...c, [k]: v }));
  const setFieldVis    = (k,v) => setConfig(c => ({ ...c, fields:       { ...c.fields,       [k]: v } }));
  const setFieldLabel  = (k,v) => setConfig(c => ({ ...c, field_labels: { ...c.field_labels, [k]: v } }));
  const setStepLabel   = (i,v) => setConfig(c => { const a=[...c.step_labels];   a[i]=v; return {...c,step_labels:a}; });
  const setStepHeading = (i,v) => setConfig(c => { const a=[...c.step_headings];  a[i]=v; return {...c,step_headings:a}; });
  const setStepSub     = (i,v) => setConfig(c => { const a=[...c.step_subtitles]; a[i]=v; return {...c,step_subtitles:a}; });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/ar-form-config", config);
      setSnack({ open:true, msg:"Form settings saved.", sev:"success" });
    } catch {
      setSnack({ open:true, msg:"Failed to save.", sev:"error" });
    } finally { setSaving(false); }
  };

  const f  = config.fields       || {};
  const fl = config.field_labels || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", mb:3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>
            Architectural Request Form Builder
          </Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Customize labels, field visibility, and advanced settings for the member AR submission form.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} sx={primaryBtnSx}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      {/* Tab navigation */}
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:3, overflow:"hidden" }}>
        <Tabs value={tab} onChange={(_,v) => setTab(v)}
          sx={{ px:1, "& .MuiTab-root":{ textTransform:"none", fontWeight:600, minHeight:46 } }}>
          <Tab icon={<LabelIcon fontSize="small" />} iconPosition="start" label="Labels & Text" />
          <Tab icon={<VisibilityIcon fontSize="small" />} iconPosition="start" label="Field Visibility" />
          <Tab icon={<TuneIcon fontSize="small" />} iconPosition="start" label="Settings" />
          <Tab icon={<PreviewIcon fontSize="small" />} iconPosition="start" label="Preview" />
        </Tabs>
      </Paper>

      {/* ── Tab 0: Labels & Text ─────────────────────────────────────────── */}
      {tab === 0 && (<>
        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Form Header</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Form Title" value={config.title}
                onChange={e => setTop("title", e.target.value)} sx={fieldSx}
                helperText="Main heading at the top of the form" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Submit Button Label" value={config.submit_label}
                onChange={e => setTop("submit_label", e.target.value)} sx={fieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Form Subtitle" value={config.subtitle}
                onChange={e => setTop("subtitle", e.target.value)} sx={fieldSx} />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Step Tabs & Headings</Typography>
          <Grid container spacing={2}>
            {[0,1,2].map(i => (
              <Grid item xs={12} key={i}>
                <Typography variant="caption" sx={{ fontWeight:700, color:"#6b778c", textTransform:"uppercase", letterSpacing:0.5 }}>
                  Step {i+1}
                </Typography>
                <Grid container spacing={1.5} sx={{ mt:0.25 }}>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth size="small" label="Tab Label" value={config.step_labels?.[i]||""}
                      onChange={e => setStepLabel(i, e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth size="small" label="Section Heading" value={config.step_headings?.[i]||""}
                      onChange={e => setStepHeading(i, e.target.value)} sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Section Subtitle" value={config.step_subtitles?.[i]||""}
                      onChange={e => setStepSub(i, e.target.value)} sx={fieldSx} />
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>Field Labels</Typography>
          <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:2 }}>
            Rename any field to match your HOA's terminology. Changes apply immediately to the member form.
          </Typography>
          <Grid container spacing={2}>
            {FIELD_GROUPS.map(group => (
              <Grid item xs={12} key={group.step}>
                <Typography variant="caption" sx={{ fontWeight:700, color:"#2f63ad", textTransform:"uppercase", letterSpacing:0.5, display:"block", mb:1 }}>
                  {group.step}
                </Typography>
                <Grid container spacing={1.5}>
                  {group.fields.map(field => (
                    <Grid item xs={12} sm={6} key={field.key}>
                      <TextField fullWidth size="small"
                        label={`"${DEFAULT_CONFIG.field_labels[field.key]}" label`}
                        value={fl[field.key] || ""}
                        onChange={e => setFieldLabel(field.key, e.target.value)}
                        sx={fieldSx}
                        InputProps={{
                          endAdornment: field.required ? (
                            <Chip label="Required" size="small"
                              sx={{ fontSize:9, height:16, bgcolor:"rgba(47,99,173,0.1)", color:"#2f63ad" }} />
                          ) : null
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </>)}

      {/* ── Tab 1: Field Visibility ───────────────────────────────────────── */}
      {tab === 1 && (
        FIELD_GROUPS.map(group => (
          <Paper elevation={0} sx={cardSx} key={group.step}>
            <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>
              {group.step}
            </Typography>
            <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:2 }}>
              Toggle which fields appear. Required fields cannot be hidden.
            </Typography>
            <Grid container spacing={1}>
              {group.fields.map(field => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                    px:1.5, py:1, borderRadius:"10px",
                    bgcolor: f[field.key] ? "rgba(47,99,173,0.04)" : "rgba(0,0,0,0.02)",
                    border:"1px solid rgba(47,99,173,0.08)" }}>
                    <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                      <Typography variant="body2" sx={{ fontWeight:600, color:"#223142" }}>
                        {fl[field.key] || DEFAULT_CONFIG.field_labels[field.key]}
                      </Typography>
                      {field.required && (
                        <Chip label="Required" size="small"
                          sx={{ fontSize:10, height:18, bgcolor:"rgba(47,99,173,0.1)", color:"#2f63ad" }} />
                      )}
                    </Box>
                    <Switch checked={Boolean(f[field.key])} disabled={field.required}
                      onChange={e => setFieldVis(field.key, e.target.checked)} size="small" />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))
      )}

      {/* ── Tab 2: Settings ──────────────────────────────────────────────── */}
      {tab === 2 && (
        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>
            Multi-Type Selection
          </Typography>
          <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:2 }}>
            When enabled, members can select multiple Request Types and Subtypes in a single submission.
            Each type can have its own subtype. The request details page will show all selected types.
          </Typography>

          <Box sx={{ p:2, borderRadius:"12px", bgcolor: config.multi_type_enabled ? "rgba(47,99,173,0.04)" : "rgba(0,0,0,0.02)",
            border:`1px solid ${config.multi_type_enabled ? "rgba(47,99,173,0.2)" : "rgba(0,0,0,0.08)"}`,
            transition:"all 0.2s" }}>
            <FormControlLabel
              control={
                <Switch checked={Boolean(config.multi_type_enabled)}
                  onChange={e => setTop("multi_type_enabled", e.target.checked)} />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>
                    Enable Multi-Type Selection
                  </Typography>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>
                    Members can add multiple request types (e.g. Fencing + Painting) in one submission
                  </Typography>
                </Box>
              }
            />
          </Box>

          {config.multi_type_enabled && (
            <Box sx={{ mt:2, p:2, borderRadius:"10px", bgcolor:"rgba(124,58,237,0.04)", border:"1px solid rgba(124,58,237,0.15)" }}>
              <Typography variant="caption" sx={{ fontWeight:700, color:"#7c3aed", display:"block", mb:0.5 }}>
                When enabled:
              </Typography>
              <Box component="ul" sx={{ m:0, pl:2.5 }}>
                {[
                  "The Request Type dropdown becomes a multi-entry panel",
                  "Members can add as many type + subtype combinations as needed",
                  "Each entry can have its own subtype selection",
                  "The first entry is also saved as the primary request_type for backwards compatibility",
                  "The AR Details page shows all selected types in a list",
                ].map((t,i) => (
                  <Typography key={i} component="li" variant="caption" sx={{ color:"#6b778c", lineHeight:1.8 }}>{t}</Typography>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* ── Tab 3: Preview ───────────────────────────────────────────────── */}
      {tab === 3 && (
        <Paper elevation={0} sx={{ ...cardSx, bgcolor:"rgba(47,99,173,0.015)" }}>
          <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:2 }}>
            <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
              <PreviewIcon sx={{ color:"#2f63ad", fontSize:18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>Form Preview</Typography>
            </Box>
            <Box sx={{ display:"flex", gap:0.5 }}>
              {[0,1,2].map(i => (
                <Button key={i} size="small" variant={previewStep===i?"contained":"outlined"}
                  onClick={() => setPreviewStep(i)}
                  sx={{ borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:11, minWidth:0, px:1.25,
                    ...(previewStep===i ? { bgcolor:"#2f63ad" } : { borderColor:"rgba(47,99,173,0.3)", color:"#2f63ad" }) }}>
                  {i+1}
                </Button>
              ))}
            </Box>
          </Box>

          <Box sx={{ border:"1px solid rgba(47,99,173,0.15)", borderRadius:"12px", p:2.5, bgcolor:"white" }}>
            <Typography variant="h6" sx={{ fontWeight:700, color:"#223142", mb:0.25 }}>{config.title}</Typography>
            <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>{config.subtitle}</Typography>

            {/* Stepper */}
            <Box sx={{ display:"flex", gap:0, mb:2.5, borderRadius:"8px", overflow:"hidden", border:"1px solid rgba(47,99,173,0.12)" }}>
              {(config.step_labels || DEFAULT_CONFIG.step_labels).map((lbl, i) => (
                <Box key={i} sx={{ flex:1, py:0.75, textAlign:"center",
                  bgcolor: i===previewStep ? "#2f63ad" : i<previewStep ? "rgba(47,99,173,0.08)" : "white",
                  borderRight: i<2 ? "1px solid rgba(47,99,173,0.12)" : "none" }}>
                  <Typography variant="caption" sx={{ fontWeight:700, fontSize:11,
                    color: i===previewStep ? "white" : i<previewStep ? "#2f63ad" : "#9ca3af" }}>
                    {i+1}. {lbl}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>
              {config.step_headings?.[previewStep]}
            </Typography>
            <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
              {config.step_subtitles?.[previewStep]}
            </Typography>

            {previewStep === 0 && (
              <Box>
                {f.request_type && (
                  config.multi_type_enabled ? (
                    <Box sx={{ mb:1.5, p:1.5, borderRadius:"8px", border:"2px dashed rgba(124,58,237,0.3)", bgcolor:"rgba(124,58,237,0.03)" }}>
                      <Typography variant="caption" sx={{ color:"#7c3aed", fontWeight:700 }}>
                        + Add Request Type (multi-select mode)
                      </Typography>
                    </Box>
                  ) : <PreviewField label={fl.request_type || "Request Type"} required />
                )}
                {f.subtype  && !config.multi_type_enabled && <PreviewField label={fl.subtype || "Subtype"} />}
                {f.scheme   && <PreviewField label={fl.scheme || "Scheme"} />}
                {f.color    && <PreviewField label={fl.color || "Color"} />}
                <PreviewField label="Property ID (read-only)" />
              </Box>
            )}

            {previewStep === 1 && (
              <Box>
                {f.description  && <PreviewField label={fl.description || "Description"} required />}
                {f.comment      && <PreviewField label={fl.comment || "Comment"} />}
                {f.start_date   && <PreviewField label={fl.start_date || "Anticipated Start Date"} />}
                {f.end_date     && <PreviewField label={fl.end_date || "Anticipated Completion Date"} />}
                {f.photo_front  && <Box sx={{ mb:1.25, p:1.25, borderRadius:"8px", border:"2px dashed #90caf9", textAlign:"center" }}>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>{fl.photo_front || "Color Photo of Front"} (upload)</Typography></Box>}
                {f.photo_impact && <Box sx={{ mb:1.25, p:1.25, borderRadius:"8px", border:"2px dashed #90caf9", textAlign:"center" }}>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>{fl.photo_impact || "Color Photo of Impact Area"} (upload)</Typography></Box>}
                {f.documents    && <Box sx={{ mb:1.25, p:1.25, borderRadius:"8px", border:"2px dashed #90caf9", textAlign:"center" }}>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>{fl.documents || "Supporting Documents"} (upload)</Typography></Box>}
                {f.contractor   && <Box sx={{ mb:1.25, p:1, borderRadius:"8px", bgcolor:"rgba(47,99,173,0.04)", border:"1px solid rgba(47,99,173,0.1)" }}>
                  <Typography variant="caption" sx={{ fontWeight:700, color:"#2f63ad" }}>+ {fl.contractor || "Add Contractor Info"}</Typography></Box>}
                {f.notes        && <PreviewField label={fl.notes || "Notes"} />}
              </Box>
            )}

            {previewStep === 2 && (
              <Box>
                <Box sx={{ p:1.5, borderRadius:"8px", bgcolor:"rgba(47,99,173,0.04)", border:"1px solid rgba(47,99,173,0.1)", mb:1.5 }}>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>Request summary displayed here...</Typography>
                </Box>
                <Box sx={{ p:1.5, borderRadius:"8px", bgcolor:"rgba(234,179,8,0.06)", border:"1px solid rgba(234,179,8,0.2)" }}>
                  <Typography variant="caption" sx={{ color:"#92400e", fontWeight:600 }}>
                    Acknowledgment checkboxes (managed in Acknowledgments tab)
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ display:"flex", justifyContent:"space-between", mt:2.5 }}>
              {previewStep > 0 && <Button size="small" variant="outlined" disabled sx={{ borderRadius:"8px", textTransform:"none" }}>Back</Button>}
              <Box sx={{ flex:1 }} />
              <Button size="small" variant="contained" disabled
                sx={{ borderRadius:"8px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad" }}>
                {previewStep < 2 ? "Next" : (config.submit_label || "Submit Request")}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack(s => ({...s,open:false}))}
        anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({...s,open:false}))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ArchitecturalFormBuilder;
